/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";

// Material Dashboard 3 PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Calendar from "examples/Calendar";

// Utils
import { API_ESA_OFFICE } from "utils";
import { logoutUser } from "actions/AuthAction";

// Icons
import Icon from "@mui/material/Icon";

function KalenderEvent() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dinasId, setDinasId] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [detailEvents, setDetailEvents] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
    } else {
      logoutUser(navigate);
    }
  }, [navigate]);

  useEffect(() => {
    const fetchDetailEvents = async () => {
      if (!dinasId) return;

      setLoading(true);
      try {
        const response = await axios.get(
          `${API_ESA_OFFICE}events/detail/all?dinasId=${dinasId}`
        );
        const detailEvents = response.data || [];

        // Simpan detail events untuk sidebar
        setDetailEvents(detailEvents);

        // Convert detail_events ke format FullCalendar events
        const events = detailEvents.map((detailEvent) => {
          // Tentukan warna berdasarkan scope atau status
          let className = "info"; // default
          if (detailEvent.scope === "internal") {
            className = "primary";
          } else if (detailEvent.scope === "pemda") {
            className = "success";
          } else if (detailEvent.scope === "publik") {
            className = "warning";
          }

          // Format title dengan kegiatan dan detail_kegiatan
          let title = detailEvent.kegiatan || "Event";
          if (detailEvent.detail_kegiatan) {
            title += ` - ${detailEvent.detail_kegiatan}`;
          }
          if (detailEvent.tempat) {
            title += ` (${detailEvent.tempat})`;
          }

          return {
            title: title,
            start: detailEvent.waktu_mulai,
            end: detailEvent.waktu_finish,
            className: className,
            extendedProps: {
              kegiatan: detailEvent.kegiatan,
              detail_kegiatan: detailEvent.detail_kegiatan,
              tempat: detailEvent.tempat,
              nama_bidang: detailEvent.nama_bidang,
              nama_pembuat: detailEvent.nama_pembuat,
              jumlah_peserta: detailEvent.jumlah_peserta || 0,
            },
          };
        });

        setCalendarEvents(events);
      } catch (error) {
        console.error("Error fetching detail events:", error);
        swal({
          title: "Error",
          text: "Gagal mengambil data events",
          icon: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (dinasId) {
      fetchDetailEvents();
    }
  }, [dinasId]);

  // Filter events untuk bulan depan (next month) dari current bulan
  const getNextMonthEvents = () => {
    // Hitung bulan dan tahun berikutnya
    let nextMonth = currentMonth + 1;
    let nextYear = currentYear;

    if (nextMonth > 12) {
      nextMonth = 1;
      nextYear += 1;
    }

    return detailEvents.filter((event) => {
      const eventDate = new Date(event.waktu_mulai);
      return (
        eventDate.getMonth() + 1 === nextMonth &&
        eventDate.getFullYear() === nextYear
      );
    });
  };

  // Format tanggal untuk sidebar
  const formatEventDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date)) return "";
    return date.toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Format waktu untuk sidebar
  const formatEventTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    if (isNaN(date)) return "";
    return date.toLocaleTimeString("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle calendar view change
  const handleDatesSet = (arg) => {
    if (arg && arg.start) {
      setCurrentMonth(arg.start.getMonth() + 1);
      setCurrentYear(arg.start.getFullYear());
    }
  };

  // Get icon berdasarkan scope
  const getEventIcon = (scope) => {
    switch (scope) {
      case "internal":
        return "event";
      case "pemda":
        return "business";
      case "publik":
        return "public";
      default:
        return "event";
    }
  };

  // Hitung bulan dan tahun berikutnya untuk display
  let nextMonth = currentMonth + 1;
  let nextYear = currentYear;
  if (nextMonth > 12) {
    nextMonth = 1;
    nextYear += 1;
  }

  const nextMonthEvents = getNextMonthEvents().sort((a, b) => {
    return new Date(a.waktu_mulai) - new Date(b.waktu_mulai);
  });

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
          <MDTypography variant="body2" color="text" mt={2}>
            Memuat data...
          </MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  const monthNames = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <MDTypography variant="h4" fontWeight="bold">
            Kalender Event
          </MDTypography>
          <MDTypography variant="body2" color="text">
            Menampilkan semua event dari dinas Anda
          </MDTypography>
        </MDBox>

        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Calendar
              header={{
                title: "",
                date: "",
              }}
              initialView="dayGridMonth"
              initialDate={new Date()}
              events={calendarEvents}
              editable={false}
              selectable={false}
              eventDisplay="block"
              height="auto"
              datesSet={handleDatesSet}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <MDTypography variant="h6" fontWeight="bold" mb={2}>
                  Next events
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  mb={2}
                  display="block"
                >
                  {monthNames[nextMonth - 1]} {nextYear}
                </MDTypography>
                <Divider sx={{ mb: 2 }} />
                {nextMonthEvents.length === 0 ? (
                  <MDTypography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    py={3}
                  >
                    Tidak ada event pada bulan ini
                  </MDTypography>
                ) : (
                  <MDBox sx={{ maxHeight: "600px", overflowY: "auto" }}>
                    {nextMonthEvents.map((event, index) => (
                      <MDBox key={event.id || index} mb={2}>
                        <MDBox display="flex" gap={2}>
                          <MDBox
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: 1,
                              backgroundColor: "grey.200",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <Icon fontSize="small" color="inherit">
                              {getEventIcon(event.scope)}
                            </Icon>
                          </MDBox>
                          <MDBox flex={1}>
                            <MDTypography
                              variant="body2"
                              fontWeight="medium"
                              mb={0.5}
                            >
                              {event.detail_kegiatan || "Event"}
                            </MDTypography>
                            <MDTypography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatEventDate(event.waktu_mulai)}, at{" "}
                              {formatEventTime(event.waktu_mulai)}
                            </MDTypography>
                            {event.tempat && (
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                {event.tempat}
                              </MDTypography>
                            )}
                          </MDBox>
                        </MDBox>
                        {index < nextMonthEvents.length - 1 && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default KalenderEvent;
